import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  ArrowLeft,
  Briefcase,
  Package,
  FileText,
  Truck,
  Plus,
  X,
  User,
  CalendarDays,
  StickyNote,
} from "lucide-react";

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  active: { label: "Active", bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400" },
  completed: { label: "Completed", bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400" },
  on_hold: { label: "On Hold", bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-400" },
  cancelled: { label: "Cancelled", bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400" },
};

const CATEGORY_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  SI: { label: "Strategic Initiative", bg: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-700 dark:text-purple-400" },
  BC: { label: "B2C", bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-400" },
  BB: { label: "B2B", bg: "bg-sky-100 dark:bg-sky-900/30", text: "text-sky-700 dark:text-sky-400" },
  BD: { label: "Distributor", bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-700 dark:text-emerald-400" },
};

function formatDate(d: string | null) {
  if (!d) return "--";
  const date = new Date(d);
  return isNaN(date.getTime()) ? "--" : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function ViewJob() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [job, setJob] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [bols, setBols] = useState<any[]>([]);

  // Link pickers
  const [showWoPicker, setShowWoPicker] = useState(false);
  const [woSearch, setWoSearch] = useState("");
  const [availableWos, setAvailableWos] = useState<any[]>([]);
  const [loadingWos, setLoadingWos] = useState(false);
  const [showBolPicker, setShowBolPicker] = useState(false);
  const [bolSearch, setBolSearch] = useState("");
  const [availableBols, setAvailableBols] = useState<any[]>([]);
  const [loadingBols, setLoadingBols] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchJob = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/ops/jobs/${id}`, { headers });
      if (!res.ok) throw new Error("Failed to load job");
      const data = await res.json();
      setJob(data.job ?? data);
      setWorkOrders(data.workOrders ?? []);
      setBols(data.bols ?? []);
    } catch (err: any) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (id) fetchJob(); }, [id, token]);

  // Status update
  const updateStatus = async (status: string) => {
    await apiFetch(`/api/ops/jobs/${id}`, { method: "PATCH", headers, body: JSON.stringify({ status }) });
    fetchJob();
  };

  // Link WO
  const openWoPicker = async () => {
    setShowWoPicker(true);
    setWoSearch("");
    setLoadingWos(true);
    try {
      const res = await apiFetch("/api/ops/work-orders", { headers });
      const json = await res.json();
      const all = json.workOrders ?? json ?? [];
      const linkedIds = new Set(workOrders.map((w: any) => w.id));
      setAvailableWos(all.filter((w: any) => !linkedIds.has(w.id)));
    } catch {}
    setLoadingWos(false);
  };

  const linkWo = async (woId: number) => {
    await apiFetch(`/api/ops/work-orders/${woId}`, { method: "PATCH", headers, body: JSON.stringify({ jobId: Number(id) }) });
    setAvailableWos((p) => p.filter((w) => w.id !== woId));
    fetchJob();
  };

  const unlinkWo = async (woId: number) => {
    await apiFetch(`/api/ops/work-orders/${woId}`, { method: "PATCH", headers, body: JSON.stringify({ jobId: null }) });
    fetchJob();
  };

  // Link BOL
  const openBolPicker = async () => {
    setShowBolPicker(true);
    setBolSearch("");
    setLoadingBols(true);
    try {
      const res = await apiFetch("/api/ops/bols", { headers });
      const json = await res.json();
      const all = json.bols ?? json ?? [];
      const linkedIds = new Set(bols.map((b: any) => b.id));
      setAvailableBols(all.filter((b: any) => !linkedIds.has(b.id)));
    } catch {}
    setLoadingBols(false);
  };

  const linkBol = async (bolId: number) => {
    await apiFetch(`/api/ops/bols/${bolId}`, { method: "PATCH", headers, body: JSON.stringify({ jobId: Number(id) }) });
    setAvailableBols((p) => p.filter((b) => b.id !== bolId));
    fetchJob();
  };

  const unlinkBol = async (bolId: number) => {
    await apiFetch(`/api/ops/bols/${bolId}`, { method: "PATCH", headers, body: JSON.stringify({ jobId: null }) });
    fetchJob();
  };

  if (loading) return <div className="p-12 text-center text-muted-foreground">Loading...</div>;
  if (error || !job) return <div className="p-12 text-center text-red-500">{error || "Job not found"}</div>;

  const cfg = STATUS_CONFIG[job.status] || STATUS_CONFIG.active;
  const totalTons = bols.reduce((s: number, b: any) => s + (Number(b.net_weight_tons) || 0), 0);

  function renderPicker(
    show: boolean, setShow: (v: boolean) => void,
    searchVal: string, setSearch: (v: string) => void,
    items: any[], loadingItems: boolean,
    onLink: (id: number) => void,
    type: "wo" | "bol",
  ) {
    if (!show) return null;
    const q = searchVal.toLowerCase();
    const filtered = items.filter((i: any) => {
      if (!q) return true;
      if (type === "wo") return (i.wo_number || "").toLowerCase().includes(q) || (i.product_name || "").toLowerCase().includes(q);
      return (i.bol_number || "").toLowerCase().includes(q) || (i.customer_name || "").toLowerCase().includes(q) || (i.material_type || "").toLowerCase().includes(q);
    });
    return (
      <div className="border border-border rounded-lg bg-secondary/30 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            {type === "wo" ? "Select Work Order" : "Select BOL"}
          </span>
          <button onClick={() => setShow(false)} className="p-1 hover:bg-secondary rounded"><X size={14} className="text-muted-foreground" /></button>
        </div>
        <input type="text" value={searchVal} onChange={(e) => setSearch(e.target.value)} placeholder={type === "wo" ? "Search WO #, product..." : "Search BOL #, customer..."} className="w-full px-3 py-2 text-xs bg-background border border-border rounded-lg" autoFocus />
        <div className="max-h-48 overflow-y-auto space-y-0.5">
          {loadingItems ? <p className="text-xs text-muted-foreground text-center py-3">Loading...</p> :
            filtered.length === 0 ? <p className="text-xs text-muted-foreground text-center py-3">{items.length === 0 ? "None available" : "No matches"}</p> :
            filtered.slice(0, 25).map((i: any) => (
              <button key={i.id} onClick={() => onLink(i.id)} className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-forest-50 dark:hover:bg-forest-900/20 rounded-lg transition-colors text-left">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-mono font-semibold text-foreground shrink-0">{type === "wo" ? i.wo_number : i.bol_number}</span>
                  <span className="text-muted-foreground truncate">{type === "wo" ? (i.product_name || "") : (i.customer_name || "")}</span>
                </div>
                <Plus size={12} className="text-forest-600 shrink-0 ml-2" />
              </button>
            ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/app/ops/jobs")} className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="heading-display text-2xl text-foreground">{job.job_number}</h1>
              {job.category && CATEGORY_CONFIG[job.category] && (
                <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${CATEGORY_CONFIG[job.category].bg} ${CATEGORY_CONFIG[job.category].text}`}>
                  {CATEGORY_CONFIG[job.category].label}
                </span>
              )}
              <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{job.name}</p>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-foreground tabular-nums">{workOrders.length}</p>
          <p className="text-xs text-muted-foreground mt-1">Work Orders</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-foreground tabular-nums">{bols.length}</p>
          <p className="text-xs text-muted-foreground mt-1">BOLs</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-foreground tabular-nums">{totalTons.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground mt-1">Total Tons</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left: Job info */}
        <div className="space-y-5">
          {/* Status */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Update Status</h3>
            <select value={job.status} onChange={(e) => updateStatus(e.target.value)} className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm font-medium">
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="on_hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Details */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Briefcase size={14} className="text-forest-600" />
              Job Details
            </h3>
            <div className="space-y-3 text-sm">
              {job.client_name && (
                <div>
                  <span className="text-muted-foreground">Client</span>
                  <p className="font-medium text-foreground mt-0.5">
                    {job.client_id ? (
                      <button onClick={() => navigate(`/app/ops/clients`)} className="text-forest-600 dark:text-forest-400 hover:underline">{job.client_name}</button>
                    ) : job.client_name}
                  </p>
                </div>
              )}
              {job.description && (
                <div>
                  <span className="text-muted-foreground">Description</span>
                  <p className="font-medium text-foreground mt-0.5">{job.description}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-muted-foreground">Created</span>
                  <p className="font-medium text-foreground mt-0.5">{formatDate(job.created_at)}</p>
                </div>
                {job.start_date && (
                  <div>
                    <span className="text-muted-foreground">Start Date</span>
                    <p className="font-medium text-foreground mt-0.5">{formatDate(job.start_date)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          {job.notes && (
            <div className="bg-card border border-border rounded-xl p-5 space-y-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <StickyNote size={14} className="text-forest-600" />
                Notes
              </h3>
              <p className="text-sm text-foreground whitespace-pre-wrap">{job.notes}</p>
            </div>
          )}
        </div>

        {/* Right: WOs and BOLs */}
        <div className="space-y-5">
          {/* Work Orders */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Package size={14} className="text-forest-600" />
                Work Orders
                {workOrders.length > 0 && <span className="text-xs font-medium bg-forest-100 dark:bg-forest-900/30 text-forest-700 dark:text-forest-400 px-2 py-0.5 rounded-full">{workOrders.length}</span>}
              </h3>
              <button onClick={openWoPicker} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-forest-700 dark:text-forest-400 bg-forest-50 dark:bg-forest-900/20 hover:bg-forest-100 dark:hover:bg-forest-900/40 rounded-lg transition-colors">
                <Plus size={12} strokeWidth={2.5} /> Link WO
              </button>
            </div>
            {renderPicker(showWoPicker, setShowWoPicker, woSearch, setWoSearch, availableWos, loadingWos, linkWo, "wo")}
            {workOrders.length === 0 && !showWoPicker ? (
              <p className="text-xs text-muted-foreground">No work orders linked yet.</p>
            ) : workOrders.length > 0 ? (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">WO #</th>
                      <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Product</th>
                      <th className="text-center py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {workOrders.map((wo: any) => (
                      <tr key={wo.id} className="border-b border-border/50 hover:bg-secondary/50 cursor-pointer transition-colors" onClick={() => navigate(`/app/ops/work-orders/${wo.id}`)}>
                        <td className="py-2 px-2 font-mono font-semibold text-foreground">{wo.wo_number}</td>
                        <td className="py-2 px-2 text-muted-foreground truncate max-w-[140px]">{wo.product_name || "-"}</td>
                        <td className="py-2 px-2 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${wo.status === "completed" ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400" : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"}`}>{wo.status}</span>
                        </td>
                        <td className="py-2 px-2">
                          <button onClick={(e) => { e.stopPropagation(); unlinkWo(wo.id); }} className="p-1 text-muted-foreground/40 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded transition-colors" title="Unlink"><X size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>

          {/* BOLs */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <FileText size={14} className="text-forest-600" />
                Bills of Lading
                {bols.length > 0 && <span className="text-xs font-medium bg-forest-100 dark:bg-forest-900/30 text-forest-700 dark:text-forest-400 px-2 py-0.5 rounded-full">{bols.length}</span>}
              </h3>
              <button onClick={openBolPicker} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-forest-700 dark:text-forest-400 bg-forest-50 dark:bg-forest-900/20 hover:bg-forest-100 dark:hover:bg-forest-900/40 rounded-lg transition-colors">
                <Plus size={12} strokeWidth={2.5} /> Link BOL
              </button>
            </div>
            {renderPicker(showBolPicker, setShowBolPicker, bolSearch, setBolSearch, availableBols, loadingBols, linkBol, "bol")}
            {bols.length === 0 && !showBolPicker ? (
              <p className="text-xs text-muted-foreground">No BOLs linked yet.</p>
            ) : bols.length > 0 ? (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">BOL #</th>
                      <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Date</th>
                      <th className="text-left py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Material</th>
                      <th className="text-right py-2 px-2 font-semibold text-muted-foreground uppercase tracking-wider">Tons</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bols.map((b: any) => (
                      <tr key={b.id} className="border-b border-border/50 hover:bg-secondary/50 cursor-pointer transition-colors" onClick={() => navigate(`/app/ops/bols/${b.id}`)}>
                        <td className="py-2 px-2 font-mono font-semibold text-foreground">{b.bol_number}</td>
                        <td className="py-2 px-2 text-muted-foreground">{b.date ? new Date(b.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "-"}</td>
                        <td className="py-2 px-2 text-muted-foreground truncate max-w-[120px]">{b.material_type || "-"}</td>
                        <td className="py-2 px-2 text-right font-semibold tabular-nums text-foreground">{Number(b.net_weight_tons || 0).toFixed(2)}</td>
                        <td className="py-2 px-2">
                          <button onClick={(e) => { e.stopPropagation(); unlinkBol(b.id); }} className="p-1 text-muted-foreground/40 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded transition-colors" title="Unlink"><X size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
